import { faClose, faPenToSquare, faPlus } from "@fortawesome/free-solid-svg-icons"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { ButtonIcon } from "../../../components/Button/Button"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TimeClockCardGrid } from "../../../components/TimeClock/TimeClockCardGrid"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { useTimeClock } from "../../../hooks/TimeClock/useTimeClock"
import { getTimeClockAdjustmentCard } from "../../../Objects/TimeClock/TimeClockCards"
import type { TimeClockAdjustment } from "../../../Objects/TimeClock/TimeClockAdjustment"
import type { TimeClockAdjustmentStatusType } from "../../../Objects/TimeClock/TimeClockAdjustmentStatusType"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import GetSpace from "../../../utils/space"
import { TimeClockAdjustmentReviewPopup } from "./TimeClockAdjustmentReviewPopup"

interface TimeClockAdjustmentsPopupProps {
  onClose: () => void
  onCreate: () => void
  onReview: (id: number, isApproved: boolean, reviewNotes?: string) => Promise<boolean>
}

export const TimeClockAdjustmentsPopup: React.FC<TimeClockAdjustmentsPopupProps> = ({
  onClose,
  onCreate,
  onReview,
}) => {
  const { t, i18n } = useTranslation(["timeClock", "helpPopup"])

  const { isLoading, getAdjustments } = useTimeClock()

  const [adjustments, setAdjustments] = useState<TimeClockAdjustment[]>([])

  const [status, setStatus] = useState<TimeClockAdjustmentStatusType | "">("Pending")

  const [selected, setSelected] = useState<TimeClockAdjustment | null>(null)

  const load = useCallback(async () => {
    const result = await getAdjustments(status || undefined)

    if (result) setAdjustments(result)
  }, [getAdjustments, status])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  const cards = useMemo(
    () => adjustments.map((adjustment, index) => getTimeClockAdjustmentCard(adjustment, index, t, i18n.language)),
    [adjustments, t, i18n.language],
  )

  const handleReview = async (isApproved: boolean, reviewNotes?: string) => {
    if (!selected) return false

    const reviewed = await onReview(selected.id, isApproved, reviewNotes)

    if (!reviewed) return false

    await load()

    return true
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("timeClock:management.tab_adjustments")}
              icon={faPenToSquare}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.time-clock-adjustments"
                    title={t("helpPopup:global.timeClockAdjustments.title")}
                    description={t("helpPopup:global.timeClockAdjustments.description")}
                    bullets={t("helpPopup:global.timeClockAdjustments.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGSaveColor}
                    iconColor={THEME.colors.iconBGSecondaryColor}
                    icon={faPlus}
                    onClick={onCreate}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockAdjustmentsPopup.button.onCreate"
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockAdjustmentsPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <div className="flex flex-col gap-3 p-2">
              <Animate type={AnimationTypes.slideUpIn} duration="1.6s">
                <GenericDropDown
                  initialState={status}
                  data={[
                    { label: t("timeClock:common.adjustment_pending"), value: "Pending" },
                    { label: t("timeClock:common.adjustment_approved"), value: "Approved" },
                    { label: t("timeClock:common.adjustment_rejected"), value: "Rejected" },
                    { label: t("timeClock:management.all_status"), value: "" },
                  ]}
                  handlerFuncion={(event) => setStatus(event.target.value as TimeClockAdjustmentStatusType | "")}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockAdjustmentsPopup.dropdown.status"
                />
              </Animate>

              <Animate type={AnimationTypes.slideUpIn} duration="1.7s">
                <div>
                  <TimeClockCardGrid
                    items={cards}
                    emptyLabel={t("timeClock:management.no_adjustments")}
                    onSelect={(id) => setSelected(adjustments.find((adjustment) => adjustment.id === id) ?? null)}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockAdjustmentsPopup.card.adjustment"
                  />
                </div>
              </Animate>
            </div>
          </div>
        </Animate>
      </div>

      {selected && (
        <TimeClockAdjustmentReviewPopup
          adjustment={selected}
          isLoading={isLoading}
          onClose={() => setSelected(null)}
          onReview={handleReview}
        />
      )}
    </ModalPortal>
  )
}
